import { NgModule } from '@angular/core';
import { mapToCanActivate, RouterModule, Routes } from '@angular/router';
import { OktaAuthGuard } from '@okta/okta-angular';
import { AppContextResolver } from 'src/app/shared/app-context/app-context.resolver';
import { PubnubListenerService } from 'src/lib/pubnub-listener-service/pubnub-listener.service';
import { TcsOktaCallbackComponent } from 'src/lib/tcs-identity/components/tcs-okta-callback/tcs-okta-callback.component';
import { LogoutPageComponent } from 'src/features/login/logout-page/logout-page.component';
import { UnauthorizedPageComponent } from './core/components/unauthorized-page/unauthorized-page.component';
import { GlobalDataContextService } from './global-data-context.service';
import { Four04PageComponent } from './pages/components/four04-page/four04-page.component';
import { AppContext } from './shared/app-context/app-context.service';
import {
  AuthorizationGuard,
  grantAccess,
  grantAccessDeep,
} from './shared/authorization/authorization.guard';
import { TcsCustomPermission } from './shared/authorization/permissions';
import { CompanyCodeGuard } from './shared/client-sync/company-code.guard';

const routes: Routes = [
  {
    path: 'logout',
    component: LogoutPageComponent,
  },
  {
    path: 'login',
    redirectTo: '',
  },
  {
    path: 'implicit/callback',
    component: TcsOktaCallbackComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedPageComponent,
    data: {
      title: 'Unauthorized',
    },
  },
  {
    path: '',
    canActivateChild: [AuthorizationGuard, OktaAuthGuard],
    children: [
      {
        path: '',
        resolve: {
          pubnub: PubnubListenerService,
          global: GlobalDataContextService,
        },
        children: [
          {
            path: '',
            loadChildren: () => import('src/app/home/home.module').then((m) => m.HomeModule),
            data: {
              ...grantAccess([
                TcsCustomPermission.ManageOperations,
                TcsCustomPermission.ManageOrg,
                TcsCustomPermission.AccessInsights,
                TcsCustomPermission.AccessPublicPages,
                TcsCustomPermission.ReadonlyOperations,
              ]),
            },
          },
          {
            path: 'devkit',
            loadChildren: () =>
              import('src/features/dev-kit/dev-kit.module').then((m) => m.DevKitModule),
          },
          {
            path: 'fleet-status',
            loadChildren: () =>
              import('src/features/fleet-status/fleet-status.module').then(
                (m) => m.FleetStatusModule,
              ),
            data: {
              authorizationStrategy: 'fleet-status',
              context: AppContext.FleetStatus,
            },
            resolve: {
              appContext: AppContextResolver,
            },
          },
          {
            path: 'admin',
            loadChildren: () => import('src/app/admin/admin.module').then((m) => m.AdminModule),
          },
          {
            path: 'rtt',
            loadChildren: () => import('src/app/rtt/rtt.module').then((m) => m.RttModule),
            data: {
              authorizationStrategy: 'rtt',
              context: AppContext.Rtt,
            },
            resolve: {
              appContext: AppContextResolver,
            },
          },
          {
            path: 'support',
            loadChildren: () =>
              import('src/app/support/support.module').then((m) => m.SupportModule),
            data: {
              authorizationStrategy: 'support',
              context: AppContext.Support,
            },
            resolve: {
              appContext: AppContextResolver,
            },
          },
          {
            path: 'release-notes',
            loadChildren: () =>
              import('src/features/release-notes/release-notes.module').then(
                (m) => m.ReleaseNotesModule,
              ),
            data: {
              ...grantAccess([
                TcsCustomPermission.ManageOperations,
                TcsCustomPermission.ManageOrg,
                TcsCustomPermission.AccessInsights,
                TcsCustomPermission.AccessPublicPages,
                TcsCustomPermission.ReadonlyOperations,
              ]),
            },
          },
          {
            path: ':companyCode',
            canActivate: mapToCanActivate([CompanyCodeGuard]),
            canActivateChild: [CompanyCodeGuard],
            children: [
              {
                path: 'org',
                data: {
                  ...grantAccessDeep([TcsCustomPermission.ManageOrg]),
                  context: AppContext.Org,
                },
                loadChildren: () => import('src/app/org/org.module').then((m) => m.OrgModule),
                resolve: {
                  appContext: AppContextResolver,
                },
              },
              {
                path: 'ops',
                loadChildren: () => import('src/app/ops/ops.module').then((m) => m.OpsModule),
                data: {
                  ...grantAccessDeep([
                    TcsCustomPermission.ManageOperations,
                    TcsCustomPermission.ReadonlyOperations,
                  ]),
                  context: AppContext.Ops,
                },
                resolve: {
                  appContext: AppContextResolver,
                },
              },
              {
                path: 'insights',
                loadChildren: () =>
                  import('src/app/insights/insights.module').then((m) => m.InsightsModule),
                data: {
                  ...grantAccessDeep([TcsCustomPermission.AccessInsights]),
                  context: AppContext.Insights,
                  authorizationStrategy: 'insights',
                },
                resolve: {
                  appContext: AppContextResolver,
                },
              },
              {
                path: 'asb',
                loadChildren: () => import('src/app/asb/asb.module').then((m) => m.AsbModule),
                data: {
                  ...grantAccessDeep([TcsCustomPermission.AccessAsb]),
                  context: AppContext.Asb,
                  authorizationStrategy: 'asb',
                },
                resolve: {
                  appContext: AppContextResolver,
                },
              },
              {
                path: 'oos-rum',
                loadChildren: () =>
                  import('src/app/oos-rum/oos-rum.module').then((m) => m.OosRumModule),
                data: {
                  ...grantAccessDeep([TcsCustomPermission.AccessOosRum]),
                  context: AppContext.OosRum,
                  authorizationStrategy: 'oos-rum',
                },
                resolve: {
                  appContext: AppContextResolver,
                },
              },
            ],
          },
        ],
      },
    ],
  },
  // Public Routes:
  {
    path: '',
    data: {
      ...grantAccessDeep([TcsCustomPermission.AccessPublicPages]),
    },
    children: [
      {
        path: '**',
        component: Four04PageComponent,
        data: { title: 'Not Found' },
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
